import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { SEALS, DAY_ACCENT, SCORE_TIERS, getScoreTier, DAILY_ROUTINE_ACTIVITIES } from '../data/sealsData';
import ConfirmModal from '../components/ConfirmModal';

const AWARD_TYPES = [
  { key: 'most_creative',          labelEn: 'Most Creative Idea',      labelJa: '最もクリエイティブなアイデア' },
  { key: 'most_customer_focused',  labelEn: 'Most Customer-Focused',   labelJa: 'お客様視点賞' },
  { key: 'best_brand',             labelEn: 'Best Brand & Logo',       labelJa: 'ベストブランド＆ロゴ' },
  { key: 'best_problem_solver',    labelEn: 'Best Problem Solver',     labelJa: '問題解決賞' },
  { key: 'bravest_pitcher',        labelEn: 'Bravest Pitcher',         labelJa: 'チャレンジ精神賞' },
  { key: 'most_improved',          labelEn: 'Most Improved Idea',      labelJa: '最優秀成長賞' },
  { key: 'best_team_player',       labelEn: 'Best Team Player',        labelJa: 'ベストチームプレーヤー' },
  { key: 'most_likely_to_succeed', labelEn: 'Most Likely to Succeed',  labelJa: '成功可能性大賞' },
];

// ── Score Slider component ────────────────────────────────────────────────────
function ScoreSlider({ value, onChange, lang }) {
  const tier = getScoreTier(value);
  return (
    <div>
      {/* Big tier preview */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, padding: '0.75rem 1rem', background: tier ? `${tier.gradient.split('(')[0]}(135deg, ${tier.gradient.match(/#[a-fA-F0-9]+/g)?.[0]}15, white)` : '#F8FAFC', borderRadius: 14, border: `2px solid ${tier ? '#E2E8F0' : '#F1F5F9'}` }}>
        <div style={{ width: 52, height: 52, borderRadius: '50%', background: tier ? tier.gradient : '#E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem', flexShrink: 0, boxShadow: tier ? `0 4px 16px ${tier.shadow}` : 'none', border: '2px solid rgba(255,255,255,0.4)' }}>
          {tier ? tier.emoji : '🔒'}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 900, fontSize: '1.1rem', color: '#1e293b' }}>{value}%</div>
          <div style={{ fontWeight: 800, fontSize: '0.82rem', color: tier ? '#64748b' : '#94a3b8' }}>
            {tier ? (lang === 'ja' ? tier.ja : tier.en) : (lang === 'ja' ? 'スコアを選んでください' : 'Choose a score')}
          </div>
        </div>
        {tier && (
          <div style={{ background: tier.gradient, color: 'white', fontSize: '0.7rem', fontWeight: 900, padding: '3px 12px', borderRadius: 999 }}>
            {tier.min}–{tier.max}%
          </div>
        )}
      </div>

      {/* Slider */}
      <div style={{ position: 'relative', padding: '0 4px' }}>
        <input
          type="range"
          min={10} max={100} step={10}
          value={value}
          onChange={e => onChange(parseInt(e.target.value))}
          style={{ width: '100%', accentColor: '#FF6B35', cursor: 'pointer', height: 6 }}
        />
        {/* Tick labels */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
          {SCORE_TIERS.map(t => (
            <button
              key={t.min}
              onClick={() => onChange(t.max)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '0.7rem', fontWeight: value >= t.min && value <= t.max ? 900 : 600,
                color: value >= t.min && value <= t.max ? '#FF6B35' : '#94a3b8',
                padding: '1px 0',
                lineHeight: 1,
              }}
              title={lang === 'ja' ? t.ja : t.en}
            >
              {t.emoji}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
          {[10,20,30,40,50,60,70,80,90,100].map(v => (
            <span key={v} style={{ fontSize: '0.55rem', fontWeight: 700, color: value === v ? '#FF6B35' : '#cbd5e1', width: '10%', textAlign: 'center' }}>{v}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// SEAL GIVE MODAL  —  The main place teachers give/remove seals
// ──────────────────────────────────────────────────────────────────────────────
function SealGiveModal({ student, onClose, lang, api, onRefresh }) {
  const [stamps, setStamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null);
  const [msg, setMsg] = useState({ text: '', ok: true });
  const [editNote, setEditNote] = useState(null); // { stampId, note }
  // Per-slot scores while giving
  const [sliderScores, setSliderScores] = useState({}); // { 'key': 80 }
  const [showSlider, setShowSlider] = useState(null);   // seal.key currently open
  // Which program's seals we're giving (relevant when student has multiple programs)
  const [activeProgramId, setActiveProgramId] = useState(student.program_id);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api('get', `/teacher/students/${student.profile_slug}`);
      setStamps((res.data.stamps || []).filter(s => s.program_id === activeProgramId));
    } catch {}
    setLoading(false);
  }, [student, api, activeProgramId]);

  useEffect(() => { load(); }, [load]);

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg({ text: '', ok: true }), 2500);
  };

  const handleGive = async (seal) => {
    const score = sliderScores[seal.key] || 100;
    setWorking(seal.key);
    try {
      await api('post', '/teacher/stamps', {
        student_id: student.id,
        program_id: activeProgramId,
        day_number: seal.day,
        session: seal.session,
        stamp_type: seal.key,
        score,
      });
      const tier = getScoreTier(score);
      flash(lang === 'ja'
        ? `🎉 ${seal.session === 'morning' ? '午前' : '午後'} — ${tier?.emoji || ''} ${tier?.ja || ''} (${score}%) をあげました！`
        : `🎉 ${seal.session === 'morning' ? 'Morning' : 'Afternoon'} — ${tier?.emoji || ''} ${tier?.en || ''} (${score}%) given!`);
      setShowSlider(null);
      load();
      onRefresh();
    } catch (err) {
      flash(err.response?.data?.message || 'Error', false);
    }
    setWorking(null);
  };

  const handleDelete = (stamp, seal) => {
    setConfirm({
      title: lang === 'ja' ? 'シールを削除' : 'Remove Seal',
      message: lang === 'ja'
        ? `「${seal.ja}」シールを削除しますか？`
        : `Remove "${seal.en}" seal from ${lang === 'ja' ? (student.name_ja || student.name) : student.name}?`,
      confirmLabel: lang === 'ja' ? '削除する' : 'Remove',
      cancelLabel: lang === 'ja' ? 'キャンセル' : 'Cancel',
      danger: true,
      onConfirm: async () => {
        setConfirm(null);
        setWorking(seal.key);
        try {
          await api('delete', `/teacher/stamps/${stamp.id}`);
          flash(lang === 'ja' ? 'シールを削除しました' : 'Seal removed');
          load();
          onRefresh();
        } catch { flash('Error', false); }
        setWorking(null);
      },
    });
  };

  const handleSaveNote = async () => {
    try {
      await api('put', `/teacher/stamps/${editNote.stampId}`, { note: editNote.note });
      flash(lang === 'ja' ? '💬 コメントを保存しました' : '💬 Note saved!');
      setEditNote(null);
      load();
    } catch { flash('Error', false); }
  };

  const displayName = lang === 'ja' ? (student.name_ja || student.name) : student.name;
  const totalEarned = stamps.length;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
      backdropFilter: 'blur(6px)',
    }}>
      <div style={{
        background: 'white',
        borderRadius: 28,
        width: '100%', maxWidth: 680,
        maxHeight: '92vh',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 40px 100px rgba(0,0,0,0.3)',
      }}>

        {/* ── Header ── */}
        <div style={{
          background: 'linear-gradient(135deg, #FF6B35, #FFD166)',
          padding: '1.5rem',
          display: 'flex', alignItems: 'center', gap: '1rem',
          flexShrink: 0,
        }}>
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', border: '2px solid rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', flexShrink: 0 }}>
            👤
          </div>
          <div style={{ flex: 1, color: 'white' }}>
            <div style={{ fontWeight: 900, fontSize: '1.15rem', lineHeight: 1.1 }}>{displayName}</div>
            <div style={{ opacity: 0.85, fontWeight: 700, fontSize: '0.82rem', marginTop: 3 }}>
              🌟 {lang === 'ja' ? `シール ${totalEarned}/10 獲得済み` : `${totalEarned}/10 seals earned`}
              {totalEarned === 10 && ' 🎉'}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%',
            width: 38, height: 38, color: 'white', fontSize: '1.1rem',
            cursor: 'pointer', fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.15s',
          }}>✕</button>
        </div>

        {/* ── Program switcher (shown when student has multiple programs) ── */}
        {student.programs?.length > 1 && (
          <div style={{ background: '#1e293b', padding: '0.6rem 1.25rem', display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', fontWeight: 700, alignSelf: 'center', marginRight: 4 }}>
              {lang === 'ja' ? 'プログラム：' : 'Program:'}
            </span>
            {student.programs.map(p => (
              <button
                key={p.id}
                onClick={() => { setActiveProgramId(p.id); setSliderScores({}); setShowSlider(null); }}
                style={{
                  background: activeProgramId === p.id ? 'white' : 'rgba(255,255,255,0.12)',
                  color: activeProgramId === p.id ? '#1e293b' : 'rgba(255,255,255,0.75)',
                  border: 'none', borderRadius: 20, padding: '0.3rem 0.9rem',
                  fontWeight: 800, fontSize: '0.78rem', cursor: 'pointer',
                }}
              >
                {p.icon || '📚'} {lang === 'ja' ? (p.name_ja || p.name) : p.name}
              </button>
            ))}
          </div>
        )}

        {/* ── Flash message ── */}
        {msg.text && (
          <div style={{
            padding: '0.7rem 1.5rem',
            background: msg.ok ? '#F0FDF4' : '#FEF2F2',
            color: msg.ok ? '#16a34a' : '#dc2626',
            fontWeight: 800, fontSize: '0.85rem',
            borderBottom: '1px solid #F1F5F9',
            flexShrink: 0,
          }}>
            {msg.text}
          </div>
        )}

        {/* ── Note editor ── */}
        {editNote && (
          <div style={{ padding: '0.75rem 1.5rem', background: '#ECFEFF', borderBottom: '1px solid #A5F3FC', flexShrink: 0 }}>
            <div style={{ fontWeight: 800, fontSize: '0.82rem', color: '#0e7490', marginBottom: 6 }}>
              💬 {lang === 'ja' ? 'コメントを追加・編集' : 'Add / edit a personal note for this seal'}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={editNote.note}
                onChange={e => setEditNote(n => ({ ...n, note: e.target.value }))}
                placeholder={lang === 'ja' ? 'よくできました！とても素晴らしかったよ！' : 'Great job! You were amazing today!'}
                style={{ flex: 1, borderRadius: 10, border: '1.5px solid #67E8F9', padding: '0.45rem 0.75rem', fontSize: '0.85rem', outline: 'none', fontFamily: 'inherit' }}
                onKeyDown={e => e.key === 'Enter' && handleSaveNote()}
              />
              <button onClick={handleSaveNote} style={{ background: '#0e7490', color: 'white', border: 'none', borderRadius: 10, padding: '0.45rem 1rem', fontWeight: 800, cursor: 'pointer', fontSize: '0.82rem' }}>
                {lang === 'ja' ? '保存' : 'Save'}
              </button>
              <button onClick={() => setEditNote(null)} style={{ background: '#E2E8F0', color: '#64748b', border: 'none', borderRadius: 10, padding: '0.45rem 0.75rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.82rem' }}>
                ✕
              </button>
            </div>
          </div>
        )}

        {/* ── Seal Grid ── */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '1.25rem 1.5rem' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
              <div style={{ fontSize: '2.5rem', animation: 'spin 1s linear infinite' }}>🌟</div>
              <div style={{ marginTop: '0.75rem', fontWeight: 700 }}>{lang === 'ja' ? '読み込み中...' : 'Loading...'}</div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 700, marginBottom: '1.25rem' }}>
                {lang === 'ja'
                  ? '🌟 シールをタップして生徒に渡す。✏️でコメントを追加、🗑️で削除'
                  : '🌟 Tap a seal to give it · ✏️ to add a note · 🗑️ to remove'}
              </div>

              {/* One section per day */}
              {[1, 2, 3, 4, 5].map(dayNum => {
                const accent = DAY_ACCENT[dayNum];
                const daySeals = SEALS.filter(s => s.day === dayNum);

                return (
                  <div key={dayNum} style={{ marginBottom: '1rem' }}>
                    {/* Day divider */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: accent, color: 'white', fontWeight: 900, fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: `0 3px 10px ${accent}50` }}>
                        {dayNum}
                      </div>
                      <div style={{ height: 2, flex: 1, background: `${accent}25`, borderRadius: 1 }} />
                    </div>

                    {/* Two seal slots */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {daySeals.map(seal => {
                        const stamp = stamps.find(s => s.day_number === seal.day && s.session === seal.session);
                        const isBusy = working === seal.key;
                        const earned = !!stamp;
                        const isOpen = showSlider === seal.key;
                        const score = sliderScores[seal.key] || 100;
                        const tier = earned ? getScoreTier(stamp.score) : getScoreTier(score);

                        return (
                          <div key={seal.key} style={{
                            borderRadius: 16,
                            border: `2px solid ${earned ? accent + '50' : isOpen ? accent + '40' : '#E2E8F0'}`,
                            background: earned ? `${accent}08` : 'white',
                            overflow: 'hidden',
                            transition: 'all 0.2s',
                          }}>
                            {/* Row: icon + info + actions */}
                            <div style={{ padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: 12 }}>
                              {/* Circle */}
                              <div style={{ width: 48, height: 48, borderRadius: '50%', flexShrink: 0, background: earned && tier ? tier.gradient : isOpen && tier ? tier.gradient : 'linear-gradient(135deg, #E2E8F0, #CBD5E1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', border: earned || isOpen ? '2px solid rgba(255,255,255,0.4)' : '2px dashed #CBD5E1', boxShadow: (earned || isOpen) && tier ? `0 4px 12px ${tier.shadow}` : 'none', transition: 'all 0.3s', position: 'relative', overflow: 'hidden' }}>
                                {(earned || isOpen) && tier ? (
                                  <>
                                    <div style={{ position: 'absolute', top: -3, left: -3, width: '40%', height: '40%', background: 'rgba(255,255,255,0.25)', borderRadius: '50%', filter: 'blur(3px)' }} />
                                    {tier.emoji}
                                  </>
                                ) : <span style={{ opacity: 0.25, fontSize: '1rem' }}>🔒</span>}
                              </div>

                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                                  <span style={{ background: earned ? accent : isOpen ? accent + '30' : '#E2E8F0', color: earned ? 'white' : isOpen ? accent : '#94a3b8', fontSize: '0.58rem', fontWeight: 800, padding: '1px 7px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    {seal.session === 'morning' ? (lang === 'ja' ? '☀️ 午前' : '☀️ Morning') : (lang === 'ja' ? '🌅 午後' : '🌅 Afternoon')}
                                  </span>
                                  {earned && stamp && (
                                    <span style={{ background: tier?.gradient || '#E2E8F0', color: 'white', fontSize: '0.58rem', fontWeight: 900, padding: '1px 8px', borderRadius: 999 }}>
                                      {stamp.score}%
                                    </span>
                                  )}
                                </div>
                                <div style={{ fontWeight: 900, fontSize: '0.85rem', color: earned ? '#1e293b' : '#64748b' }}>
                                  {earned && tier ? (lang === 'ja' ? tier.ja : tier.en) : (lang === 'ja' ? seal.ja : seal.en)}
                                </div>
                                {earned && stamp?.note && <div style={{ fontSize: '0.62rem', color: '#0e7490', fontWeight: 600, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>💬 {stamp.note}</div>}
                              </div>

                              {/* Right actions */}
                              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                {earned ? (
                                  <>
                                    <button onClick={() => setEditNote({ stampId: stamp.id, note: stamp.note || '' })} style={{ background: '#F1F5F9', border: 'none', borderRadius: 8, padding: '5px 9px', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 700 }} title={lang === 'ja' ? 'コメント' : 'Note'}>✏️</button>
                                    <button onClick={() => handleDelete(stamp, seal)} disabled={isBusy} style={{ background: '#FEF2F2', border: 'none', borderRadius: 8, padding: '5px 9px', fontSize: '0.75rem', cursor: 'pointer', color: '#ef4444', fontWeight: 700 }} title={lang === 'ja' ? '削除' : 'Remove'}>{isBusy ? '…' : '🗑️'}</button>
                                  </>
                                ) : (
                                  <button
                                    onClick={() => setShowSlider(isOpen ? null : seal.key)}
                                    style={{ background: isOpen ? accent : `${accent}15`, color: isOpen ? 'white' : accent, border: 'none', borderRadius: 10, padding: '6px 14px', fontWeight: 900, fontSize: '0.78rem', cursor: 'pointer', transition: 'all 0.15s' }}
                                  >
                                    {isOpen ? (lang === 'ja' ? '▲ 閉じる' : '▲ Close') : (lang === 'ja' ? '🌟 あげる' : '🌟 Give')}
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Slider panel (not earned, open) */}
                            {!earned && isOpen && (
                              <div style={{ borderTop: `2px solid ${accent}20`, padding: '1rem', background: `${accent}05` }}>
                                <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#64748b', marginBottom: '0.75rem' }}>
                                  {lang === 'ja' ? '📊 評価スコアを選んでください' : '📊 Choose a performance score'}
                                </div>
                                <ScoreSlider
                                  value={score}
                                  onChange={v => setSliderScores(prev => ({ ...prev, [seal.key]: v }))}
                                  lang={lang}
                                />
                                <button
                                  onClick={() => handleGive(seal)}
                                  disabled={isBusy}
                                  style={{
                                    width: '100%', marginTop: '0.85rem',
                                    background: `linear-gradient(135deg, ${accent}, ${accent}CC)`,
                                    color: 'white', border: 'none', borderRadius: 12,
                                    padding: '0.7rem', fontWeight: 900, fontSize: '0.9rem',
                                    cursor: isBusy ? 'wait' : 'pointer',
                                    boxShadow: `0 4px 16px ${accent}40`,
                                  }}
                                >
                                  {isBusy ? '⏳' : (lang === 'ja' ? `🌟 ${score}% でシールをあげる！` : `🌟 Give Seal at ${score}%!`)}
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={{ padding: '1rem 1.5rem', borderTop: '2px solid #F1F5F9', display: 'flex', gap: 10, justifyContent: 'flex-end', flexShrink: 0 }}>
          <Link
            to={`/student/${student.profile_slug}`}
            className="btn-outline-summer text-decoration-none"
            style={{ fontSize: '0.82rem', padding: '0.5rem 1.2rem' }}
          >
            👁 {lang === 'ja' ? 'プロフィールを見る' : 'View Profile'}
          </Link>
          <button onClick={onClose} className="btn-summer" style={{ fontSize: '0.82rem', padding: '0.5rem 1.4rem' }}>
            {lang === 'ja' ? '完了' : 'Done'}
          </button>
        </div>
      </div>

      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        cancelLabel={confirm?.cancelLabel}
        danger={confirm?.danger}
        onConfirm={confirm?.onConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

// ── Award Modal ──────────────────────────────────────────────────────────────
function AwardModal({ student, onClose, lang, api, onRefresh }) {
  const [awardType, setAwardType] = useState('most_creative');
  const [programId, setProgramId] = useState(student.programs?.[0]?.id || student.program_id || '');
  const [done, setDone] = useState(false);

  const giveAward = async () => {
    const award = AWARD_TYPES.find(a => a.key === awardType);
    try {
      await api('post', '/teacher/awards', {
        student_id: student.id,
        program_id: programId || null,
        award_type: awardType,
        award_label: award.labelEn,
        award_label_ja: award.labelJa,
      });
      setDone(true);
      setTimeout(() => { onRefresh(); onClose(); }, 1800);
    } catch {}
  };

  const displayName = lang === 'ja' ? (student.name_ja || student.name) : student.name;
  const selectedAward = AWARD_TYPES.find(a => a.key === awardType);

  if (done) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
        <div style={{ background: 'white', borderRadius: 24, padding: '2.5rem', maxWidth: 380, width: '100%', textAlign: 'center', boxShadow: '0 30px 80px rgba(0,0,0,0.2)' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🏆</div>
          <h4 style={{ fontWeight: 900, color: 'var(--orange)' }}>{lang === 'ja' ? '表彰しました！' : 'Award Given!'}</h4>
          <p style={{ fontWeight: 700, color: '#64748b', fontSize: '0.9rem' }}>
            <strong>{displayName}</strong>{lang === 'ja' ? 'さんに' : ' received '}
            <strong>{lang === 'ja' ? selectedAward?.labelJa : selectedAward?.labelEn}</strong>
            {lang === 'ja' ? 'を贈りました 🎉' : ' 🎉'}
          </p>
          <p style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: 600 }}>
            {lang === 'ja'
              ? '✅ 生徒のプロフィールの「シール」タブに表示されます。'
              : '✅ This award now appears on the student\'s profile under the Seals tab.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', backdropFilter: 'blur(6px)' }}>
      <div style={{ background: 'white', borderRadius: 24, maxWidth: 460, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,0.25)' }}>
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #FFD166, #FF6B35)', padding: '1.25rem 1.5rem', color: 'white' }}>
          <div style={{ fontWeight: 900, fontSize: '1.1rem' }}>🏆 {lang === 'ja' ? '特別表彰' : 'Give Special Award'}</div>
          <div style={{ fontWeight: 700, fontSize: '0.85rem', opacity: 0.9, marginTop: 3 }}>→ {displayName}</div>
        </div>

        {/* Info banner */}
        <div style={{ padding: '0.75rem 1.25rem', background: '#FFFBEB', borderBottom: '1px solid #FEF3C7', fontSize: '0.78rem', fontWeight: 700, color: '#92400e' }}>
          ℹ️ {lang === 'ja'
            ? 'アワードは生徒のシールパスポートに表示されます。本人と保護者が確認できます。'
            : 'Awards appear on the student\'s Seal Passport — visible to the student and their guardian.'}
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '1rem 1.25rem' }}>
          {/* Program picker (if multiple) */}
          {student.programs?.length > 1 && (
            <div className="mb-3">
              <label style={{ fontWeight: 800, fontSize: '0.8rem', color: 'var(--muted)', display: 'block', marginBottom: 6 }}>
                {lang === 'ja' ? 'プログラムを選択' : 'For which program?'}
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {student.programs.map(p => (
                  <button key={p.id} type="button" onClick={() => setProgramId(p.id)} style={{
                    padding: '4px 12px', borderRadius: 999, fontWeight: 700, fontSize: '0.78rem',
                    border: `2px solid ${programId === p.id ? 'var(--orange)' : '#E2E8F0'}`,
                    background: programId === p.id ? '#FFF0E0' : 'white',
                    color: programId === p.id ? 'var(--orange)' : '#64748b',
                    cursor: 'pointer',
                  }}>
                    {lang === 'ja' ? (p.name_ja || p.name) : p.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Award type selection */}
          <label style={{ fontWeight: 800, fontSize: '0.8rem', color: 'var(--muted)', display: 'block', marginBottom: 8 }}>
            {lang === 'ja' ? 'アワードを選んでください' : 'Select an award:'}
          </label>
          {AWARD_TYPES.map(a => (
            <button key={a.key} onClick={() => setAwardType(a.key)} style={{
              display: 'block', width: '100%', textAlign: 'left', marginBottom: 7,
              borderRadius: 12, padding: '0.65rem 1rem', fontWeight: 700, fontSize: '0.87rem',
              border: `2px solid ${awardType === a.key ? 'var(--orange)' : '#E2E8F0'}`,
              background: awardType === a.key ? '#FFF0E0' : 'white',
              color: awardType === a.key ? 'var(--orange)' : '#1e293b',
              cursor: 'pointer', transition: 'all 0.15s',
            }}>
              🏆 {lang === 'ja' ? a.labelJa : a.labelEn}
            </button>
          ))}
        </div>

        {/* Footer */}
        <div style={{ padding: '1rem 1.25rem', borderTop: '1px solid #F1F5F9', background: '#FAFBFC' }}>
          <div style={{ marginBottom: 10, padding: '0.6rem 1rem', background: '#FFF0E0', borderRadius: 12, fontSize: '0.8rem', fontWeight: 800, color: 'var(--orange)' }}>
            🏆 {displayName} → {lang === 'ja' ? selectedAward?.labelJa : selectedAward?.labelEn}
          </div>
          <div className="d-flex gap-2">
            <button className="btn-summer flex-fill" onClick={giveAward}>
              {lang === 'ja' ? '🏆 表彰する！' : '🏆 Give This Award!'}
            </button>
            <button className="btn-outline-summer" onClick={onClose} style={{ padding: '0.5rem 1rem' }}>✕</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Teacher Dashboard ───────────────────────────────────────────────────
export default function TeacherDashboard() {
  const { t, lang, user, api } = useApp();
  const navigate = useNavigate();
  const [tab, setTab] = useState('students');
  const [routineDay, setRoutineDay] = useState(1);
  const [routineStamps, setRoutineStamps] = useState([]); // { id, student_id, day_number, session }
  const [routineProgramId, setRoutineProgramId] = useState(null);
  const [routineWorking, setRoutineWorking] = useState(null); // 'studentId-session'
  const [students, setStudents] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sealModal, setSealModal] = useState(null);
  const [awardModal, setAwardModal] = useState(null);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState(null);
  const [programsLoaded, setProgramsLoaded] = useState(false);
  // Add student form
  const [newName, setNewName] = useState('');
  const [newNameJa, setNewNameJa] = useState('');
  const [newProgramIds, setNewProgramIds] = useState([]);
  const [addResult, setAddResult] = useState(null);
  // Edit student
  const [editStudent, setEditStudent] = useState(null); // { id, name, name_ja, profile_slug }
  const [editMsg, setEditMsg] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [forcingCert, setForcingCert] = useState(null);

  const forceCertificate = async (student) => {
    const next = !student.certificate_unlocked;
    setForcingCert(student.id);
    try {
      await api('patch', `/admin/students/${student.id}/force-certificate`, { unlocked: next });
      loadData();
    } catch {}
    setForcingCert(null);
  };

  useEffect(() => {
    if (!user || (user.role !== 'teacher' && user.role !== 'admin')) { navigate('/login'); return; }
    loadData();
    loadPrograms();
  }, [user]);

  useEffect(() => {
    if (tab === 'routine') loadRoutineStamps();
  }, [tab]);

  const loadPrograms = async () => {
    setProgramsLoaded(false);
    try {
      const res = await api('get', '/teacher/programs');
      if (res.data?.length) {
        const routine = res.data.find(p => p.slug === 'daily-routine');
        if (routine) setRoutineProgramId(routine.id);
        // Exclude daily-routine from manual enrollment list — it's given per-activity
        setPrograms(res.data.filter(p => p.slug !== 'daily-routine'));
      }
    } catch {}
    setProgramsLoaded(true);
  };

  const loadRoutineStamps = async () => {
    try {
      const res = await api('get', '/teacher/routine-stamps');
      setRoutineStamps(res.data || []);
    } catch {}
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [studRes, statsRes] = await Promise.all([
        api('get', '/teacher/students'),
        user.role === 'admin' ? api('get', '/admin/stats') : Promise.resolve(null),
      ]);
      setStudents(studRes.data);
      if (statsRes) setStats(statsRes.data);
    } catch {}
    setLoading(false);
  };

  const handleRoutineToggle = async (student, activity, dayNum) => {
    const wk = `${student.id}-${activity.session}-${dayNum}`;
    if (routineWorking === wk) return;
    const existing = routineStamps.find(
      s => s.student_id === student.id && s.day_number === dayNum && s.session === activity.session
    );
    setRoutineWorking(wk);
    try {
      if (existing) {
        await api('delete', `/teacher/stamps/${existing.id}`);
        setRoutineStamps(prev => prev.filter(s => s.id !== existing.id));
      } else {
        if (!routineProgramId) return;
        const res = await api('post', '/teacher/stamps', {
          student_id: student.id,
          program_id: routineProgramId,
          day_number: dayNum,
          session: activity.session,
          stamp_type: activity.key,
          score: 100,
        });
        // Optimistic add — reload to get real id
        await loadRoutineStamps();
      }
    } catch {}
    setRoutineWorking(null);
  };

  const toggleProgram = (id) => {
    setNewProgramIds(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const addStudent = async (e) => {
    e.preventDefault();
    try {
      const res = await api('post', '/admin/students', {
        name: newName, name_ja: newNameJa,
        teacher_id: user.id,
        program_ids: newProgramIds,
      });
      setAddResult(res.data);
      setNewName(''); setNewNameJa(''); setNewProgramIds([]);
      loadData();
    } catch (err) {
      setAddResult({ error: err.response?.data?.message || 'Error' });
    }
  };

  const saveEditStudent = async () => {
    try {
      await api('patch', `/admin/students/${editStudent.id}`, {
        name: editStudent.name,
        name_ja: editStudent.name_ja,
      });
      setEditMsg(lang === 'ja' ? '✅ 更新しました' : '✅ Updated!');
      setTimeout(() => { setEditStudent(null); setEditMsg(''); }, 1200);
      loadData();
    } catch {
      setEditMsg(lang === 'ja' ? '❌ エラーが発生しました' : '❌ Error saving');
    }
  };

  const deleteStudent = (student) => {
    const name = lang === 'ja' ? (student.name_ja || student.name) : student.name;
    setConfirm({
      title: lang === 'ja' ? '生徒を削除' : 'Delete Student',
      message: lang === 'ja' ? `「${name}」を削除しますか？この操作は取り消せません。` : `Delete "${name}"? This cannot be undone.`,
      confirmLabel: lang === 'ja' ? '削除する' : 'Delete',
      cancelLabel: lang === 'ja' ? 'キャンセル' : 'Cancel',
      danger: true,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await api('delete', `/admin/students/${student.id}`);
          loadData();
        } catch {}
      },
    });
  };

  const resetProgress = (student) => {
    const name = lang === 'ja' ? (student.name_ja || student.name) : student.name;
    setConfirm({
      title: lang === 'ja' ? '進捗をリセット（新セッション開始）' : 'Reset Progress (start new session)',
      message: lang === 'ja'
        ? `「${name}」の進捗をリセットして新しいセッションを開始しますか？\n\n過去のスタンプ・表彰は削除されず、そのまま記録に残ります。生徒は0%から再スタートします。`
        : `Start a new session for "${name}"?\n\nPast stamps and awards are NOT deleted — they stay on record. The student will restart at 0%.`,
      confirmLabel: lang === 'ja' ? 'リセットする' : 'Reset',
      cancelLabel: lang === 'ja' ? 'キャンセル' : 'Cancel',
      onConfirm: async () => {
        setConfirm(null);
        try {
          await api('post', `/teacher/students/${student.id}/reset-progress`);
          loadData();
        } catch {}
      },
    });
  };

  // Group rows by student ID — keep one entry per student with all their programs
  // A student may appear under multiple teacher_ids (one per program); group by student+teacher pair
  const groupedStudents = students.reduce((acc, row) => {
    // Key: studentId + teacherId (a student can belong to multiple teachers across programs)
    const key = `${row.id}-${row.teacher_id}`;
    const existing = acc.find(s => s._key === key);
    if (existing) {
      existing.programs.push({ id: row.program_id, name: row.program_name, name_ja: row.program_name_ja, stamp_count: row.stamp_count, icon: row.icon });
      existing.total_stamps = (existing.total_stamps || 0) + (row.stamp_count || 0);
      if (row.added_by_me) existing.added_by_me = true;
    } else {
      acc.push({
        ...row,
        _key: key,
        programs: [{ id: row.program_id, name: row.program_name, name_ja: row.program_name_ja, stamp_count: row.stamp_count, icon: row.icon }],
        total_stamps: row.stamp_count || 0,
        added_by_me: !!row.added_by_me,
      });
    }
    return acc;
  }, []);

  const filtered = groupedStudents.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    (s.name_ja || '').includes(search)
  );

  return (
    <div className="summer-bg pb-5">

      {/* ── Dashboard Header ── */}
      <div style={{ background: 'linear-gradient(135deg, #073B4C, #118AB2)', padding: '2.5rem 0' }}>
        <div className="container">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div style={{ color: 'white' }}>
              <h2 style={{ fontWeight: 900, margin: 0 }}>
                {user?.role === 'admin' ? '⚙️ ' + t.adminDashboard : '👩‍🏫 ' + t.teacherDashboard}
              </h2>
              <div style={{ opacity: 0.75, fontWeight: 600, marginTop: '0.3rem' }}>👋 {user?.name}</div>
            </div>
            {user?.role === 'admin' && (
              <Link to="/admin" className="btn-summer text-decoration-none">⚙️ {t.adminDashboard}</Link>
            )}
          </div>

          {/* Admin stats */}
          {stats && (
            <div className="row g-3 mt-3">
              {[
                { label: t.totalTeachers, value: stats.teachers, icon: '👩‍🏫' },
                { label: t.totalStudents, value: stats.students, icon: '🎒' },
                { label: t.totalStampsGiven, value: stats.stamps, icon: '🌟' },
              ].map((s, i) => (
                <div key={i} className="col-4">
                  <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: 16, padding: '1rem', color: 'white', textAlign: 'center', backdropFilter: 'blur(10px)' }}>
                    <div style={{ fontSize: '1.8rem' }}>{s.icon}</div>
                    <div style={{ fontSize: '1.8rem', fontWeight: 900 }}>{s.value}</div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.8, fontWeight: 600 }}>{s.label}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── How to give seals — instruction banner ── */}
      <div style={{ background: 'linear-gradient(135deg, #FFF8E1, #FFFDE7)', borderBottom: '2px solid #FFD16650' }}>
        <div className="container" style={{ padding: '0.85rem var(--bs-gutter-x, 0.75rem)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '1.4rem' }}>💡</span>
            <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#92400e' }}>
              {lang === 'ja'
                ? '🌟 シールのあげ方：生徒カードの「シールをあげる」ボタンを押して、あげたいシールを選んでクリックしてください。編集・削除もできます。'
                : '🌟 How to give seals: Click the "Give Seals" button on any student card, then click the seal you want to award. You can also add notes, edit, or remove seals.'}
            </div>
          </div>
        </div>
      </div>

      <div className="container mt-4">
        {/* Tabs */}
        <div className="tab-nav mb-4">
          <button className={`tab-btn ${tab === 'students' ? 'active' : ''}`} onClick={() => setTab('students')}>
            🎒 {lang === 'ja' ? '生徒一覧' : 'Students'}
          </button>
          <button className={`tab-btn ${tab === 'routine' ? 'active' : ''}`} onClick={() => setTab('routine')}>
            📋 {lang === 'ja' ? 'デイリールーティン' : 'Daily Routine'}
          </button>
          <button className={`tab-btn ${tab === 'add' ? 'active' : ''}`} onClick={() => setTab('add')}>
            ➕ {lang === 'ja' ? '生徒を追加' : 'Add Student'}
          </button>
        </div>

        {/* Add Student form */}
        {tab === 'add' && (
          <div className="card-summer p-4" style={{ maxWidth: 480 }}>
            <h5 style={{ fontWeight: 900, marginBottom: '1.5rem' }}>➕ {lang === 'ja' ? '新しい生徒を追加' : 'Add New Student'}</h5>
            <form onSubmit={addStudent}>
              <div className="mb-3">
                <label style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--muted)', display: 'block', marginBottom: '0.4rem' }}>Name (English) *</label>
                <input type="text" className="input-summer" value={newName} onChange={e => setNewName(e.target.value)} required placeholder="e.g. Taro Yamada" />
              </div>
              <div className="mb-3">
                <label style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--muted)', display: 'block', marginBottom: '0.4rem' }}>名前（日本語）</label>
                <input type="text" className="input-summer" value={newNameJa} onChange={e => setNewNameJa(e.target.value)} placeholder="例：山田太郎" />
              </div>
              <div className="mb-4">
                <label style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--muted)', display: 'block', marginBottom: '0.6rem' }}>
                  {lang === 'ja' ? 'プログラム（複数選択可）' : 'Programs (select 1–6)'}
                </label>
                {programs.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '1rem' }}>
                    {!programsLoaded ? (
                      <div style={{ color: '#94a3b8', fontSize: '0.85rem', fontWeight: 700 }}>⏳ {lang === 'ja' ? '読み込み中...' : 'Loading programs...'}</div>
                    ) : (
                      <div>
                        <div style={{ color: '#ef4444', fontSize: '0.85rem', fontWeight: 700, marginBottom: 8 }}>
                          ⚠️ {lang === 'ja' ? 'プログラムを読み込めませんでした' : 'Could not load programs'}
                        </div>
                        <button onClick={loadPrograms} style={{ background: 'var(--orange)', color: 'white', border: 'none', borderRadius: 10, padding: '6px 16px', fontWeight: 800, cursor: 'pointer', fontSize: '0.82rem' }}>
                          🔄 {lang === 'ja' ? '再試行' : 'Retry'}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    {/* Select All / Deselect All */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                      <button type="button"
                        onClick={() => setNewProgramIds(programs.map(p => p.id))}
                        style={{ background: '#FFF0E0', color: 'var(--orange)', border: '2px solid var(--orange)', borderRadius: 10, padding: '4px 14px', fontWeight: 800, fontSize: '0.78rem', cursor: 'pointer' }}
                      >
                        ✅ {lang === 'ja' ? '全て選択' : 'Select All'}
                      </button>
                      <button type="button"
                        onClick={() => setNewProgramIds([])}
                        style={{ background: '#F8FAFC', color: '#64748b', border: '2px solid #E2E8F0', borderRadius: 10, padding: '4px 14px', fontWeight: 800, fontSize: '0.78rem', cursor: 'pointer' }}
                      >
                        ✕ {lang === 'ja' ? '全て解除' : 'Deselect All'}
                      </button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {programs.map(p => {
                        const checked = newProgramIds.includes(p.id);
                        return (
                          <div key={p.id}
                            onClick={() => toggleProgram(p.id)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10,
                              padding: '0.6rem 1rem', borderRadius: 12,
                              border: `2px solid ${checked ? 'var(--orange)' : '#E2E8F0'}`,
                              background: checked ? '#FFF0E0' : 'white',
                              cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem',
                              transition: 'all 0.15s', userSelect: 'none',
                            }}
                          >
                            <div style={{
                              width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                              background: checked ? 'var(--orange)' : 'white',
                              border: `2px solid ${checked ? 'var(--orange)' : '#CBD5E1'}`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'all 0.15s',
                            }}>
                              {checked && <span style={{ color: 'white', fontSize: '0.75rem', fontWeight: 900, lineHeight: 1 }}>✓</span>}
                            </div>
                            <span style={{ fontSize: '1.1rem' }}>{p.icon || '📚'}</span>
                            <span style={{ color: checked ? 'var(--orange)' : '#1e293b' }}>
                              {lang === 'ja' ? (p.name_ja || p.name) : p.name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    {/* Counter — always show */}
                    <div style={{ marginTop: 10, padding: '0.5rem 1rem', borderRadius: 10, background: newProgramIds.length > 0 ? '#FFF0E0' : '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: 800, fontSize: '0.82rem', color: newProgramIds.length > 0 ? 'var(--orange)' : '#94a3b8' }}>
                        {newProgramIds.length > 0
                          ? `✅ ${newProgramIds.length} / ${programs.length} ${lang === 'ja' ? 'プログラム選択済み' : 'programs selected'}`
                          : (lang === 'ja' ? '⚠️ プログラムを1つ以上選んでください' : '⚠️ Please select at least 1 program')}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <button type="submit" className="btn-summer w-100">➕ {lang === 'ja' ? '生徒を追加する' : 'Add Student'}</button>
            </form>
            {addResult && (
              <div style={{ marginTop: '1rem', padding: '1rem', background: addResult.error ? '#FEF2F2' : '#F0FDF4', borderRadius: 16, fontWeight: 700 }}>
                {addResult.error ? (
                  <div style={{ color: '#dc2626' }}>{addResult.error}</div>
                ) : (
                  <>
                    <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem', color: '#16a34a' }}>✅ {lang === 'ja' ? '生徒を追加しました！' : 'Student added!'}</div>
                    <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 4 }}>{lang === 'ja' ? 'ログインコード：' : 'Login Code:'}</div>
                    <div style={{ fontSize: '2.5rem', fontWeight: 900, color: 'var(--orange)', letterSpacing: '6px', fontFamily: 'monospace' }}>
                      {addResult.login_code}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 4 }}>
                      {lang === 'ja' ? 'このコードを生徒に渡してください' : 'Share this code with the student'}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Daily Routine Tab ── */}
        {tab === 'routine' && (
          <div>
            {/* Day selector */}
            <div style={{ display: 'flex', gap: 8, marginBottom: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontWeight: 800, fontSize: '0.85rem', color: 'var(--muted)', marginRight: 4 }}>
                {lang === 'ja' ? '📅 日を選択：' : '📅 Day:'}
              </span>
              {[1,2,3,4,5].map(d => (
                <button key={d}
                  onClick={() => setRoutineDay(d)}
                  style={{
                    padding: '0.45rem 1.1rem', borderRadius: 12, fontWeight: 900, fontSize: '0.9rem',
                    border: `2px solid ${routineDay === d ? DAY_ACCENT[d] : '#E2E8F0'}`,
                    background: routineDay === d ? DAY_ACCENT[d] : 'white',
                    color: routineDay === d ? 'white' : '#64748b',
                    cursor: 'pointer', transition: 'all 0.15s',
                    boxShadow: routineDay === d ? `0 4px 14px ${DAY_ACCENT[d]}50` : 'none',
                  }}
                >
                  {lang === 'ja' ? `${d}日目` : `Day ${d}`}
                </button>
              ))}
            </div>

            {/* Activity legend */}
            <div style={{ display: 'flex', gap: 12, marginBottom: '1.25rem', flexWrap: 'wrap' }}>
              {DAILY_ROUTINE_ACTIVITIES.map(act => (
                <div key={act.key} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 20, background: '#F8FAFC', border: '1.5px solid #E2E8F0', fontSize: '0.78rem', fontWeight: 700, color: '#475569' }}>
                  <span style={{ fontSize: '1rem' }}>{act.emoji}</span>
                  {lang === 'ja' ? act.ja : act.en}
                  <span style={{ opacity: 0.55, fontWeight: 600 }}>{act.time}</span>
                </div>
              ))}
            </div>

            {!routineProgramId && (
              <div style={{ padding: '2rem', textAlign: 'center', background: '#FEF9C3', borderRadius: 16, fontWeight: 700, color: '#92400e' }}>
                ⚠️ {lang === 'ja' ? 'デイリールーティンプログラムがデータベースに見つかりません。管理者にお問い合わせください。' : 'Daily Routine program not found in database. Please contact admin.'}
              </div>
            )}

            {routineProgramId && groupedStudents.length === 0 && (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                <div style={{ fontSize: '2rem' }}>🎒</div>
                <div style={{ fontWeight: 700, marginTop: 8 }}>{lang === 'ja' ? 'まだ生徒がいません' : 'No students yet'}</div>
              </div>
            )}

            {routineProgramId && groupedStudents.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {groupedStudents.map(student => {
                  const displayName = lang === 'ja' ? (student.name_ja || student.name) : student.name;
                  return (
                    <div key={student.id} style={{ background: 'white', borderRadius: 18, border: '1.5px solid #F1F5F9', padding: '0.85rem 1.1rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                      <div style={{ fontWeight: 800, fontSize: '0.92rem', minWidth: 120, flex: '1 1 auto' }}>
                        <span style={{ fontSize: '1rem', marginRight: 6 }}>👤</span>{displayName}
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {DAILY_ROUTINE_ACTIVITIES.map(act => {
                          const done = routineStamps.some(
                            s => s.student_id === student.id && s.day_number === routineDay && s.session === act.session
                          );
                          const wk = `${student.id}-${act.session}-${routineDay}`;
                          const working = routineWorking === wk;
                          return (
                            <button key={act.key}
                              onClick={() => handleRoutineToggle(student, act, routineDay)}
                              disabled={working}
                              title={lang === 'ja' ? `${act.ja} (${act.time})` : `${act.en} (${act.time})`}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 5,
                                padding: '0.45rem 0.9rem', borderRadius: 12, fontWeight: 800, fontSize: '0.8rem',
                                border: `2px solid ${done ? '#06D6A0' : '#E2E8F0'}`,
                                background: done ? 'linear-gradient(135deg, #06D6A0, #059669)' : '#F8FAFC',
                                color: done ? 'white' : '#94a3b8',
                                cursor: working ? 'wait' : 'pointer',
                                transition: 'all 0.18s',
                                opacity: working ? 0.6 : 1,
                                boxShadow: done ? '0 3px 10px rgba(6,214,160,0.3)' : 'none',
                              }}
                            >
                              <span style={{ fontSize: '1.1rem' }}>{working ? '⏳' : done ? '✅' : act.emoji}</span>
                              <span style={{ display: 'none' }}>{lang === 'ja' ? act.ja : act.en}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Student cards */}
        {tab === 'students' && loading ? (
          <div className="text-center py-5">
            <div style={{ fontSize: '2.5rem', animation: 'spin 1s linear infinite' }}>🌟</div>
          </div>
        ) : tab === 'students' && filtered.length === 0 ? (
          <div className="text-center py-5 card-summer p-5">
            <div style={{ fontSize: '3rem' }}>🎒</div>
            <h5 style={{ fontWeight: 700, color: 'var(--muted)', marginTop: '1rem' }}>
              {lang === 'ja' ? 'まだ生徒がいません' : 'No students yet'}
            </h5>
          </div>
        ) : tab === 'students' ? (
          <div>
            {/* Search + count */}
            <div className="mb-4 d-flex align-items-center gap-3 flex-wrap">
              <input type="text" className="input-summer" placeholder={lang === 'ja' ? '🔍 生徒名で検索...' : '🔍 Search students...'} value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 360 }} />
              <span style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--muted)' }}>
                🎒 {filtered.length} {lang === 'ja' ? '名の生徒' : 'students'}
              </span>
            </div>

            <div className="row g-3">
              {filtered.map(student => {
                const displayName = lang === 'ja' ? (student.name_ja || student.name) : student.name;
                const totalSeals = student.total_stamps || 0;
                const maxSeals = (student.programs?.length || 1) * 10;
                const pct = maxSeals > 0 ? Math.round((totalSeals / maxSeals) * 100) : 0;

                return (
                  <div key={student.id} className="col-md-6 col-lg-4">
                    <div className="card-summer" style={{ padding: 0, overflow: 'hidden', height: '100%' }}>

                      {/* Header */}
                      <div style={{ padding: '1rem 1.1rem 0.85rem', borderBottom: '1px solid #F1F5F9' }}>
                        <div className="d-flex align-items-center gap-3">
                          <div style={{
                            width: 46, height: 46, borderRadius: '50%', flexShrink: 0,
                            background: student.added_by_me
                              ? 'linear-gradient(135deg, var(--orange), var(--coral))'
                              : 'linear-gradient(135deg, #64748b, #94a3b8)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '1.3rem', border: '2px solid white',
                            boxShadow: '0 4px 12px rgba(100,116,139,0.25)',
                          }}>👤</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 900, fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {displayName}
                            </div>
                            <div style={{ fontSize: '0.68rem', color: 'var(--muted)', fontWeight: 600, marginTop: 2, display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                              🎒 {student.programs?.length || 0} {lang === 'ja' ? 'プログラム' : 'programs'}
                              <span style={{
                                background: student.added_by_me ? '#FFF0E0' : '#F1F5F9',
                                color: student.added_by_me ? 'var(--orange)' : '#64748b',
                                borderRadius: 999, padding: '1px 7px', fontWeight: 800, fontSize: '0.6rem',
                                border: student.added_by_me ? '1px solid #FFD16660' : '1px solid #E2E8F0',
                              }}>
                                👩‍🏫 {lang === 'ja' ? (student.teacher_name_ja || student.teacher_name) : student.teacher_name}
                                {student.added_by_me && <span style={{ opacity: 0.7 }}>{lang === 'ja' ? '（自分）' : ' (you)'}</span>}
                              </span>
                            </div>
                          </div>
                          {/* Overall % badge */}
                          <div style={{ textAlign: 'center', flexShrink: 0 }}>
                            <div style={{ fontSize: '1.1rem', fontWeight: 900, color: pct >= 87 ? '#06D6A0' : 'var(--orange)', lineHeight: 1 }}>{pct}%</div>
                            <div style={{ fontSize: '0.55rem', color: 'var(--muted)', fontWeight: 700 }}>{lang === 'ja' ? '総合' : 'overall'}</div>
                          </div>
                        </div>

                        {/* Overall progress bar */}
                        <div style={{ marginTop: '0.75rem' }}>
                          <div style={{ height: 6, background: '#F1F5F9', borderRadius: 999, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: pct >= 87 ? 'linear-gradient(90deg, #06D6A0, #118AB2)' : 'linear-gradient(90deg, #FF6B35, #FFD166)', borderRadius: 999, transition: 'width 0.6s ease' }} />
                          </div>
                        </div>
                      </div>

                      {/* Program pills */}
                      <div style={{ padding: '0.65rem 1rem', borderBottom: '1px solid #F1F5F9', display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                        {student.programs?.map(p => (
                          <span key={p.id} style={{ fontSize: '0.7rem', fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: '#FFF0E0', color: 'var(--orange)', border: '1.5px solid #FFD16650' }}>
                            {lang === 'ja' ? (p.name_ja || p.name) : p.name}
                            {p.stamp_count > 0 && <span style={{ marginLeft: 4, opacity: 0.7 }}>· {p.stamp_count}🌟</span>}
                          </span>
                        ))}
                      </div>

                      {/* Action buttons — any teacher/admin can manage any student */}
                      <div style={{ padding: '0.75rem 1rem', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button
                          onClick={() => setSealModal({ ...student, program_id: student.programs?.[0]?.id })}
                          style={{
                            flex: '1 1 auto', background: 'linear-gradient(135deg, #FF6B35, #FFD166)',
                            color: 'white', border: 'none', borderRadius: 12, padding: '0.6rem 0.8rem',
                            fontWeight: 900, fontSize: '0.82rem', cursor: 'pointer',
                            boxShadow: '0 4px 16px rgba(255,107,53,0.25)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                          }}
                        >
                          🌟 {lang === 'ja' ? 'シールをあげる' : 'Give Seals'}
                        </button>

                        {/* View profile */}
                        <Link to={`/student/${student.profile_slug}`} className="btn-outline-summer text-decoration-none"
                          style={{ fontSize: '0.78rem', padding: '0.5rem 0.8rem', flexShrink: 0 }}>👁</Link>

                        <button onClick={() => setAwardModal({ ...student })}
                          style={{ background: 'white', borderRadius: 10, flexShrink: 0, border: '2px solid #FFD166', color: '#F59E0B', fontWeight: 800, fontSize: '0.78rem', padding: '0.45rem 0.65rem', cursor: 'pointer' }}
                          title={lang === 'ja' ? '表彰する' : 'Give Award'}>
                          🏆
                        </button>
                        <button onClick={() => forceCertificate(student)} disabled={forcingCert === student.id}
                          title={student.certificate_unlocked ? (lang === 'ja' ? '修了証を取り消す' : 'Revoke certificate') : (lang === 'ja' ? '修了証を強制解放' : 'Force unlock certificate')}
                          style={{ background: student.certificate_unlocked ? '#DCFCE7' : '#FFF0E0', color: student.certificate_unlocked ? '#16A34A' : '#F59E0B', border: `1.5px solid ${student.certificate_unlocked ? '#86EFAC' : '#FDE68A'}`, borderRadius: 8, flexShrink: 0, padding: '0.45rem 0.65rem', fontWeight: 800, fontSize: '0.78rem', cursor: 'pointer' }}>
                          {student.certificate_unlocked ? '🏆✓' : '🔓'}
                        </button>
                        <button onClick={() => setEditStudent({ id: student.id, name: student.name, name_ja: student.name_ja || '', profile_slug: student.profile_slug })}
                          style={{ background: 'white', borderRadius: 10, flexShrink: 0, border: '2px solid #A5F3FC', color: '#0891b2', fontWeight: 800, fontSize: '0.78rem', padding: '0.45rem 0.65rem', cursor: 'pointer' }}
                          title={lang === 'ja' ? '編集' : 'Edit'}>
                          ✏️
                        </button>
                        <button onClick={() => resetProgress(student)}
                          style={{ background: 'white', borderRadius: 10, flexShrink: 0, border: '2px solid #DDD6FE', color: '#7C3AED', fontWeight: 800, fontSize: '0.78rem', padding: '0.45rem 0.65rem', cursor: 'pointer' }}
                          title={lang === 'ja' ? '進捗をリセット（新セッション開始）' : 'Reset Progress (start new session)'}>
                          🔄
                        </button>
                        <button onClick={() => deleteStudent(student)}
                          style={{ background: 'white', borderRadius: 10, flexShrink: 0, border: '2px solid #FECACA', color: '#ef4444', fontWeight: 800, fontSize: '0.78rem', padding: '0.45rem 0.65rem', cursor: 'pointer' }}
                          title={lang === 'ja' ? '削除' : 'Delete'}>
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>

      {/* ── Edit Student Modal ── */}
      {editStudent && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', backdropFilter: 'blur(6px)' }}>
          <div style={{ background: 'white', borderRadius: 24, width: '100%', maxWidth: 420, padding: '2rem', boxShadow: '0 30px 80px rgba(0,0,0,0.25)' }}>
            <h5 style={{ fontWeight: 900, marginBottom: '1.5rem' }}>✏️ {lang === 'ja' ? '生徒を編集' : 'Edit Student'}</h5>
            <div className="mb-3">
              <label style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--muted)', display: 'block', marginBottom: '0.4rem' }}>Name (English)</label>
              <input className="input-summer" value={editStudent.name} onChange={e => setEditStudent(s => ({ ...s, name: e.target.value }))} />
            </div>
            <div className="mb-4">
              <label style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--muted)', display: 'block', marginBottom: '0.4rem' }}>名前（日本語）</label>
              <input className="input-summer" value={editStudent.name_ja} onChange={e => setEditStudent(s => ({ ...s, name_ja: e.target.value }))} />
            </div>
            {editMsg && (
              <div style={{ marginBottom: '1rem', fontWeight: 700, fontSize: '0.85rem', color: editMsg.startsWith('✅') ? '#16a34a' : '#dc2626' }}>{editMsg}</div>
            )}
            <div className="d-flex gap-2">
              <button className="btn-summer flex-fill" onClick={saveEditStudent}>{lang === 'ja' ? '💾 保存' : '💾 Save'}</button>
              <button className="btn-outline-summer flex-fill" onClick={() => { setEditStudent(null); setEditMsg(''); }}>✕ {lang === 'ja' ? 'キャンセル' : 'Cancel'}</button>
            </div>
            <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#FEF2F2', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#ef4444' }}>🗑️ {lang === 'ja' ? '生徒を削除' : 'Delete student'}</span>
              <button
                onClick={() => { deleteStudent(editStudent); setEditStudent(null); }}
                style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: 8, padding: '4px 14px', fontWeight: 800, fontSize: '0.78rem', cursor: 'pointer' }}
              >
                {lang === 'ja' ? '削除する' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {sealModal && (
        <SealGiveModal
          student={sealModal}
          onClose={() => setSealModal(null)}
          lang={lang}
          api={api}
          onRefresh={loadData}
        />
      )}
      {awardModal && (
        <AwardModal
          student={awardModal}
          onClose={() => setAwardModal(null)}
          lang={lang}
          api={api}
          onRefresh={loadData}
        />
      )}

      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        cancelLabel={confirm?.cancelLabel}
        danger={confirm?.danger}
        onConfirm={confirm?.onConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
